import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, PartyPopper } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';
import api, { apiUrl } from '../utils/apiClient';

const PaymentCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [planName, setPlanName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyPayment = async () => {
      const reference = searchParams.get('reference') || searchParams.get('trxref');

      if (!reference) {
        setStatus('failed');
        setError('No payment reference found');
        return;
      }

      try {
        const { data } = await api.get(apiUrl(`/payments/verify/${reference}`));

        if (data.data?.status === 'success') {
          setStatus('success');
          const paymentType = data.data?.metadata?.payment_type;
          if (paymentType === 'marketplace') {
            setPlanName(data.data?.metadata?.listing_title || 'Study Material');
          } else if (paymentType === 'tutor') {
            setPlanName('Tutor Session');
          } else {
            setPlanName(data.data?.metadata?.plan || 'Premium');
          }

          // Trigger confetti
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
        } else {
          setStatus('failed');
          setError(data.data?.gateway_response || 'Payment was not successful');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setStatus('failed');
        setError('Unable to verify payment. Please contact support.');
      }
    };

    verifyPayment();
  }, [searchParams]);

  return (
    <div className="min-h-screen cyber-bg flex items-center justify-center p-4">
      <div className="glass-card rounded-3xl p-8 max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="mb-6">
              <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-white mb-2">
              Verifying Payment...
            </h2>
            <p className="text-white/60">
              Please wait while we confirm your payment
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <div className="relative inline-block">
                <CheckCircle className="w-16 h-16 text-[#CCFF00] mx-auto" />
                <PartyPopper className="w-8 h-8 text-yellow-400 absolute -top-2 -right-2 animate-bounce" />
              </div>
            </div>
            <h2 className="font-outfit text-2xl font-bold text-white mb-2">
              {planName === 'Study Material' || planName === 'Tutor Session'
                ? `${planName} Purchased!`
                : `Welcome to ${planName}!`} 🎉
            </h2>
            <p className="text-white/60 mb-6">
              {planName === 'Study Material'
                ? 'Your purchase is ready. Check your library!'
                : planName === 'Tutor Session'
                  ? 'Session booked! Check your dashboard for details.'
                  : 'Your subscription is now active. Let\'s supercharge your studying!'}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/app')}
                className="w-full h-12 rounded-full bg-gradient-to-r from-[#CCFF00] to-[#00F0FF] text-black font-bold glow-button"
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate('/lectures')}
                variant="outline"
                className="w-full h-12 rounded-full border-white/20 text-white hover:bg-white/10"
              >
                Start Recording a Lecture
              </Button>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="mb-6">
              <XCircle className="w-16 h-16 text-red-400 mx-auto" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-white mb-2">
              Payment Failed
            </h2>
            <p className="text-white/60 mb-2">{error}</p>
            <p className="text-white/40 text-sm mb-6">
              Don't worry, no charges were made. Please try again.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/#pricing')}
                className="w-full h-12 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold"
              >
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/app')}
                variant="outline"
                className="w-full h-12 rounded-full border-white/20 text-white/70 hover:bg-white/10"
              >
                Continue with Free Plan
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallbackPage;
